import _, {intersection} from 'lodash';
import {createSelector} from 'reselect';

import {shouldPopout} from '../../../libs/shared/helpers/src/environment/platformHelpers';
import {AppParametricSelector} from '../../helpers/reselectHelpers';
import {LocalPreferencesSizesEnum} from '../../models/api/localPreferencesModel';
import {AppState} from '../appState';
import {getSizes} from '../data/localPreferences/localPreferencesSelectors';
import {CellsPanelPageTypesEnum} from './cells/cellsPanelState';
import {
  panelIsCellsPanel,
  panelIsComposerPanel,
  panelIsInboxesPanel,
  panelIsViewerPanel,
  PanelState,
} from './panelsState';
import {NewPanelId, PanelTypesEnum, RAppPanelId, tryFindAppPanelIdInPanelId} from './panelStateBase';

/*
 * General purpose panel selectors.
 */

/** Get the map of all panels. */
export function getPanels(state: AppState) {
  return state.panels;
}

/** Get a flat list of all panels. */
export const getFlatPanels = createSelector(getPanels, (panels) =>
  _(panels)
    .values()
    .compact()
    .sortBy((panel) => panel.id)
    .value(),
);

interface PanelProps {
  panelId: NewPanelId;
}

/** Find the current panel. */
export function getPanel(state: AppState, props: PanelProps): PanelState | undefined {
  return state.panels[props.panelId];
}

/** Find the ID of the App panel that we're currently in. */
export function makeGetAppPanelIdFromPanel() {
  const getAppPanelIdFromPanel = createSelector(getPanel, (panel) => {
    if (!panel) {
      throw new Error('No panel found.');
    }

    // If this is a standalone (popup) panel, use the specified AppPanelId.
    const {popupAppPanelId} = panel;
    if (popupAppPanelId) {
      return popupAppPanelId;
    }

    throw new Error('An AppPanelId could not be found.');
  });

  return (state: AppState, props: PanelProps) => {
    const {panelId} = props;
    if (RAppPanelId.is(panelId)) {
      return panelId;
    }

    // Otherwise, extract the App panel ID from the panel ID.
    const appPanelId = tryFindAppPanelIdInPanelId(panelId);
    if (appPanelId) {
      return appPanelId;
    }

    return getAppPanelIdFromPanel(state, props);
  };
}

/**
 * Find the first app panel ID we can find.
 *
 * @deprecated Pass around panel IDs and use makeGetAppPanelIdFromPanel() instead.
 */
export const getOptionalFirstAppPanelId = createSelector(getFlatPanels, (panels) =>
  _(panels)
    .map((panel) => (panel.type === PanelTypesEnum.APP ? panel.id : undefined))
    .find(),
);

/**
 * Find the first app panel ID we can find. Throws when no app panel is found.
 *
 * @deprecated Pass around panel IDs and use makeGetAppPanelIdFromPanel() instead.
 */
export const getFirstAppPanelId = createSelector(getOptionalFirstAppPanelId, (appPanelId) => {
  if (!appPanelId) {
    throw new Error('Failed to find any app panel.');
  }

  return appPanelId;
});

/**
 * Get the ID of the first inboxes panel, if any.
 *
 * @deprecated Pass around panel IDs instead.
 */
export const getInboxesPanelId = createSelector(
  getFlatPanels,
  (panels) => panels.find(panelIsInboxesPanel)?.id,
);

/**
 * Get the first cells panel, if any.
 *
 * @deprecated Pass around panel IDs instead.
 */
export const getCellsPanel = createSelector(getFlatPanels, (panels) => panels.find(panelIsCellsPanel));
export const getCellsPanels = createSelector(getFlatPanels, (panels) => panels.filter(panelIsCellsPanel));

/**
 * @deprecated Pass around panel IDs instead.
 */
export const getViewerPanel = createSelector(getFlatPanels, (panels) => panels.find(panelIsViewerPanel));

/** Get the workspaces width */
export const getWorkspacesWidth = createSelector(
  getSizes,
  (sizes) => sizes[LocalPreferencesSizesEnum.WORKSPACES],
);

/*
 * Sub-property getters.
 */

export function makeGetIsMessagePopup<T>(getPanelId: AppParametricSelector<T, NewPanelId>) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];
    return Boolean(
      panel &&
        panel.popupAppPanelId &&
        (panel.type === PanelTypesEnum.MESSAGE || panel.type === PanelTypesEnum.EML),
    );
  });
}

export function makeGetIsMessagePopout<T>(getPanelId: AppParametricSelector<T, NewPanelId>) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];
    return Boolean(
      panel &&
        panel.popupAppPanelId &&
        (panel.type === PanelTypesEnum.MESSAGE || panel.type === PanelTypesEnum.EML) &&
        shouldPopout(panel.shouldForcePopout),
    );
  });
}

export function makeGetIsComposerPanelPopup<T>(getPanelId: AppParametricSelector<T, NewPanelId>) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];
    return Boolean(panel && panel.popupAppPanelId && panel.type === PanelTypesEnum.COMPOSER);
  });
}

export function makeGetIsComposerPanelPopout<T>(getPanelId: AppParametricSelector<T, NewPanelId>) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];
    return Boolean(
      panel &&
        panel.popupAppPanelId &&
        panel.type === PanelTypesEnum.COMPOSER &&
        shouldPopout(panel.shouldForcePopout),
    );
  });
}

export function makeGetIsPanelPopup<T>(getPanelId: AppParametricSelector<T, NewPanelId>) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];
    return Boolean(panel && panel.popupAppPanelId);
  });
}

export function makeGetIsPanelPopout<T>(getPanelId: AppParametricSelector<T, NewPanelId>) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];
    return Boolean(panel && panel.popupAppPanelId && shouldPopout(panel.shouldForcePopout));
  });
}

export function makeGetIsInComposerPanel(getPanelId: AppParametricSelector<any, NewPanelId>) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];
    return Boolean(panel && panelIsComposerPanel(panel));
  });
}

export function makeGetIsStandalonePanel(getPanelId: AppParametricSelector<any, NewPanelId>) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];
    return Boolean(panel && panelIsComposerPanel(panel) && panel.isStandalone);
  });
}

export const getCellsPanelPageConversationState = createSelector(getCellsPanel, (cellsPanel) => {
  const currentPage = cellsPanel && (cellsPanel.pages.current || cellsPanel.pages.next);

  if (!currentPage || currentPage.type !== CellsPanelPageTypesEnum.CONVERSATIONS) {
    return undefined;
  }

  return currentPage;
});

export function makeGetCellsPanelsWithConversationIds(
  getConversationIds: AppParametricSelector<any, ReadonlyArray<number>>,
) {
  return createSelector([getConversationIds, getCellsPanels], (conversationIds, cellsPanels) =>
    _(cellsPanels)
      .filter((cellsPanel) => {
        const currentPage = cellsPanel.pages.current || cellsPanel.pages.next;

        if (!currentPage || currentPage.type !== CellsPanelPageTypesEnum.CONVERSATIONS) {
          return false;
        }

        const conversationIdsInCommon = intersection(currentPage.conversationIds, conversationIds);

        return conversationIdsInCommon.length > 0;
      })
      .value(),
  );
}

export function makeGetConversationOverlaysForPanelId(getPanelId: AppParametricSelector<any, NewPanelId>) {
  return createSelector([getPanelId, getPanels], (panelId, panels) => {
    const panel = panels[panelId];
    if (!panel) {
      return {};
    }

    if (!panelIsCellsPanel(panel)) {
      return {};
    }

    return (
      (panel.pages.current &&
        panel.pages.current.type === CellsPanelPageTypesEnum.CONVERSATIONS &&
        panel.pages.current.conversationOverlays) ||
      {}
    );
  });
}
